import { ChevronDown } from "lucide-react"
import React, { useState, useEffect } from "react"
import { useBooking } from "@/context/booking"

const Header: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isSticky, setIsSticky] = useState(false)
  const [currentPath, setCurrentPath] = useState("")

  useEffect(() => {
    setCurrentPath(window.location.pathname)

    const handleScroll = () => {
      setIsSticky(window.scrollY > 300)
    }

    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  const { openModal } = useBooking()

  const menuItems = [
    {
      title: "Về Thiền Trúc",
      href: "/",
      submenu: [{ title: "Hơi thở Thiền Trúc", href: "/thien-truc-breath" }],
    },
    {
      title: "Thưởng vị",
      href: "/thuc-don-tan-dinh",
      submenu: [
        { title: "Menu Tân Định", href: "/thuc-don-tan-dinh" },
        { title: "Menu Thủ Đức", href: "/thuc-don-thu-duc" },
      ],
    },
    {
      title: "Không gian",
      href: "/dia-diem",
      submenu: [],
    },
    {
      title: "Đặt bàn",
      href: "#dat-ban",
      submenu: [],
      onClick: () => openModal(),
    },
  ].map(item => ({
    ...item,
    active: item.href === currentPath,
  }))

  const isMenuOverviewPage =
    currentPath === "/thuc-don-tan-dinh" ||
    currentPath === "/thuc-don-thu-duc" ||
    currentPath === "/dia-diem"
  const isLocationPage = currentPath === "/dia-diem"
  const useWhiteBranding = !isSticky && (isMenuOverviewPage || isLocationPage)

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 font-semibold text-xl ${
        isSticky ? "bg-[#FEF5EA]" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1300px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between py-4">
          {/* Logo */}
          <div className="flex items-center">
            <a
              href="/"
              className={`transition-opacity duration-300 ${
                isSticky ? "opacity-0 absolute" : "opacity-100"
              }`}
            >
              <img
                width="144"
                height="82"
                src={
                  useWhiteBranding
                    ? "/logos/web-TT-logo-white.png"
                    : "./logos/logo.png"
                }
                className="h-12 sm:h-16 w-auto"
                alt="Thien Truc Logo"
              />
            </a>

            <a
              href="/"
              className={`transition-opacity duration-300 ${
                isSticky ? "opacity-100" : "opacity-0 absolute"
              }`}
            >
              <img
                width="144"
                height="82"
                src="./logos/logo.png"
                className="h-12 sm:h-16 w-auto"
                alt="Thien Truc Logo"
              />
            </a>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            <ul className="flex items-center space-x-8">
              {menuItems.map((item, index) => (
                <li key={index} className="relative group">
                  <a
                    href={item.href}
                    className={`flex items-center space-x-1 py-2 transition-colors duration-300 ${
                      isSticky
                        ? "text-primary"
                        : isMenuOverviewPage
                        ? "text-white hover:text-white"
                        : "text-primary hover:text-primary"
                    } ${
                      item.active
                        ? isSticky
                          ? "text-primary"
                          : isMenuOverviewPage
                          ? "text-white"
                          : "text-primary"
                        : ""
                    }`}
                    aria-current={item.active ? "page" : undefined}
                    onClick={e => {
                      if (item.onClick) {
                        e.preventDefault()
                        item.onClick()
                      }
                    }}
                  >
                    <span>{item.title}</span>
                    {item.submenu.length > 0 && (
                      <ChevronDown className="size-5" />
                    )}
                  </a>

                  {/* Dropdown Menu */}
                  {item.submenu.length > 0 && (
                    <ul
                      className={`absolute top-full left-0 mt-2 min-w-max ${
                        isSticky
                          ? "bg-[#FEF5EA] shadow-lg"
                          : "bg-transparent shadow-none"
                      } rounded-md opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50`}
                    >
                      {item.submenu.map((subItem, subIndex) => (
                        <li key={subIndex}>
                          <a
                            href={subItem.href}
                            className={`block px-4 py-3 transition-colors duration-200 whitespace-nowrap ${
                              useWhiteBranding
                                ? "text-white hover:text-white hover:bg-transparent"
                                : "text-[#115544] hover:text-[#115544] hover:bg-transparent"
                            }`}
                          >
                            {subItem.title}
                          </a>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden flex items-center justify-center w-10 h-10 p-2"
            onClick={() => setIsMobileMenuOpen(true)}
            aria-label="Menu Toggle"
          >
            <svg
              className={`w-6 h-6 ${
                useWhiteBranding ? "stroke-white" : "text-[#115544]"
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile Off-Canvas Menu */}
      <div
        className={`fixed inset-0 z-[100] lg:hidden transition-all duration-300 ${
          isMobileMenuOpen ? "visible" : "invisible"
        }`}
      >
        {/* Overlay */}
        <div
          className={`absolute inset-0 bg-black transition-opacity duration-300 ${
            isMobileMenuOpen ? "opacity-50" : "opacity-0"
          }`}
          onClick={() => setIsMobileMenuOpen(false)}
        />

        {/* Off-canvas panel */}
        <div
          className={`absolute top-0 right-0 h-full w-80 max-w-[90vw] bg-white shadow-xl transform transition-transform duration-300 ${
            isMobileMenuOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="flex flex-col h-full">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-lg font-semibold text-gray-800">Menu</h2>
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="flex items-center justify-center w-8 h-8 text-[#115544]"
                aria-label="Close menu"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Navigation */}
            <nav className="flex-1 py-6">
              <ul className="space-y-2">
                {menuItems.map((item, index) => (
                  <li key={index}>
                    <a
                      href={item.href}
                      className={`block px-6 py-3 text-gray-800 hover:bg-[#115544]/10 hover:text-[#115544] font-semibold transition-colors duration-200 ${
                        item.active ? "bg-[#115544]/10 text-[#115544]" : ""
                      }`}
                      onClick={e => {
                        if (item.onClick) {
                          e.preventDefault()
                          item.onClick()
                        }
                        setIsMobileMenuOpen(false)
                      }}
                    >
                      {item.title}
                    </a>
                    {item.submenu.length > 0 && (
                      <ul className="ml-4 mt-2 space-y-1">
                        {item.submenu.map((subItem, subIndex) => (
                          <li key={subIndex}>
                            <a
                              href={subItem.href}
                              className="block px-6 py-2 text-sm text-gray-600 hover:text-[#115544] transition-colors duration-200"
                              onClick={() => setIsMobileMenuOpen(false)}
                            >
                              {subItem.title}
                            </a>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            </nav>
          </div>
        </div>
      </div>
    </header>
  )
}

export default Header
